import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import Button from '../../components/Buttons/Button';
import DataTable from '../../components/DataTable/DataTable';
import { IColInfo } from '../../components/DataTable/Interfaces';
import ButtonBar from '../../components/Fields/ButtonBar';
import Form from '../../components/Fields/Form';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import Title from '../../components/Headers/Title';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../components/Spinner/SpinnerOverlay';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import AttachmentModel from '../../models/AttachmentModel';
import AttachmentVersionModel from '../../models/AttachmentVersionModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { DataResult } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';
import Attachment_popup_ReviseAttachment from './Popups/Attachment_popup_ReviseAttachment';

const AttachmentView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<DataResult<AttachmentModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();
    const [formData, setFormData] = useState<AttachmentModel>();
    const [showPopup, setShowPopup] = useState<boolean>(false)
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
    const { isAuthorizedForAttachmentType } = useAuth();

    let idParams: IdParams = useParams();
    let attachmentId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        attachmentId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (attachmentId) {
            getAttachment(attachmentId)
        }
    }, [attachmentId]);

    function getAttachment(attachmentId: number) {
        DataService.getById<AttachmentModel>('api/v1/attachments/', attachmentId)
            .then(response => setFormData({ ...response }))
            .catch(e => {
                if (e.response?.data?.status === 401)
                    setIsAuthorized(false);
                else
                    handleException(e);
            });
    }

    function submit() {
        if (formData) {
            DataService
                .update<AttachmentModel>(`api/v1/attachments/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => handleException(e));
        }
    }

    function downloadLatestAttachmentVersion() {
        if (formData && formData.attachmentVersions) {
            let rowData: AttachmentVersionModel = formData.attachmentVersions[0];
            formData.attachmentVersions.forEach((v) => {
                if (formData.latestVersion === v.versionNumber)
                    rowData = v;
            })
            downloadAttachmentVersion(rowData)
        }
    }

    function downloadAttachmentVersion(rowData: AttachmentVersionModel) {
        if (rowData.fileUrl) {
            const link = document.createElement('a');
            link.href = rowData.fileUrl;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    }

    function downloadAsImageset() {
        if (formData && formData.attachmentVersions) {
            console.log('formData:', formData)
            let rowData: AttachmentVersionModel = formData.attachmentVersions[0];
            formData.attachmentVersions.forEach((v) => {
                if (formData.latestVersion === v.versionNumber)
                    rowData = v;
            })

            if (rowData) {
                const url = 'api/v1/Attachments/getImageset/' + rowData.fileId;

                var filename = formData?.name + '-V' + rowData.versionNumber + '.zip'

                DataService
                    .getFile(url)
                    .then(response => {
                        console.log('response', response)
                        if (response instanceof Blob) {
                            const url = window.URL.createObjectURL(response);
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', filename);
                            document.body.appendChild(link);
                            link.click();
                        }
                        else {
                            handleResult(response)
                        }

                    })
                    .catch(e => handleException(e));
            }
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'attachmentType.name', label: 'Attachment type' },
            { name: 'latestVersion', label: 'Versionnumber' },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const colInfos: IColInfo[] = [
        { key: true, dataProp: 'id', visible: false },
        { dataProp: 'thumbnailUrl', dataType: 'bigimage', sort: false },
        { title: 'Version', dataProp: 'versionNumber', sort: 'desc', dataType: 'number', width: 100 },
        { title: 'Comment', dataProp: 'comment' },
        { title: 'Filename', dataProp: 'fileName' },
        { title: 'Created by', dataProp: 'createdByUser', dataType: 'user', width: 'auto' },
        { title: 'Created on', dataProp: 'createdOn', dataType: 'datetime' },

    ];


    if (formData &&  isAuthorizedForAttachmentType(ApplicationFunctions.DownloadAttachment, formData.attachmentTypeId)) {
        colInfos.push(
            { rowButtons: [{ text: "Download", icon: 'fa-cloud-download', color: '#89C4F4', onClick: downloadAttachmentVersion }] },
            { dataProp: 'fileTypeUrl', dataType: 'bigimage', sort: false, width: 96 },
        );
    }

    const { getFieldProps } = useValidation(formConfig, formData, serverErrors);

    if (!isAuthorized)
        return <Unauthorized />;

    if (!formData)
        return <SpinnerOverlay />

    return (
        <div className='main-content'>
            <Form title='Attachment'>
                <Container fluid>
                    <Row>
                        <Col md='4'>
                            {formData.thumbnailUrl && <img src={formData.thumbnailUrl} style={{ width: '100%' }} />}
                        </Col>
                        <Col md='8'>
                            <TextboxField {...getFieldProps('name')} />
                            <TextboxField {...getFieldProps('attachmentType.name')} />
                            <TextboxField {...getFieldProps('latestVersion')} />
                            <ButtonBar offset={2}>
                                <Authorize applicationFunction={ApplicationFunctions.CreateAttachment} attachmentTypeId={formData.attachmentTypeId}>
                                    <Button type='button' text='Revise' onClick={() => setShowPopup(true)} />
                                </Authorize>
                                <Authorize applicationFunction={ApplicationFunctions.DownloadAttachment} attachmentTypeId={formData.attachmentTypeId}>
                                    <Button type='button' text='Download' onClick={() => downloadLatestAttachmentVersion()} />
                                    <Button type='button' text='Download as Imageset' onClick={() => downloadAsImageset()} />
                                </Authorize>
                            </ButtonBar>
                        </Col>
                    </Row>
                    <Row style={{ marginTop: 12 }}>
                        <Col md='12'>
                            <Title text="Versions" size={5} />
                            <DataTable
                                name='dtAttachmentAttachmentVersions'
                                colInfos={colInfos}
                                tableData={formData.attachmentVersions}
                            />
                        </Col>
                    </Row>
                </Container>
            </Form>
            {showPopup && <Attachment_popup_ReviseAttachment attachmentId={formData.id} show={showPopup} onHide={() => { setShowPopup(false); getAttachment(formData.id); }} />}
        </div>
    );
}
export default AttachmentView;